import React, { useState, useEffect } from 'react';
import { X, Circle } from 'lucide-react';

interface TicTacToeProps {
  difficulty: 'easy' | 'medium' | 'hard';
  onWin: () => void;
  onLose: () => void;
  onDraw: () => void;
}

type Cell = null | 'X' | 'O';
type Board = Cell[];

export function TicTacToe({ difficulty, onWin, onLose, onDraw }: TicTacToeProps) {
  const [board, setBoard] = useState<Board>(Array(9).fill(null));
  const [isPlayerTurn, setIsPlayerTurn] = useState(true);
  const [gameOver, setGameOver] = useState(false);
  const [winLine, setWinLine] = useState<number[] | null>(null);
  const [lastMove, setLastMove] = useState<number | null>(null);
  const [showResult, setShowResult] = useState(false);

  const checkWinner = (squares: Board): { winner: Cell; line: number[] | null } => {
    const lines = [
      [0, 1, 2], [3, 4, 5], [6, 7, 8], // rows
      [0, 3, 6], [1, 4, 7], [2, 5, 8], // columns
      [0, 4, 8], [2, 4, 6] // diagonals
    ];

    for (const [a, b, c] of lines) {
      if (squares[a] && squares[a] === squares[b] && squares[a] === squares[c]) {
        return { winner: squares[a], line: [a, b, c] };
      }
    }
    return { winner: null, line: null };
  };

  const evaluatePosition = (squares: Board, depth: number): number => {
    const { winner, line } = checkWinner(squares);
    
    if (winner === 'O') return 10 - depth; // AI wins
    if (winner === 'X') return depth - 10; // Player wins
    if (squares.every(square => square !== null)) return 0; // Draw
    
    // Additional positional evaluation
    let score = 0;
    
    // Center control
    if (squares[4] === 'O') score += 3;
    
    // Corner control
    const corners = [0, 2, 6, 8];
    corners.forEach(corner => {
      if (squares[corner] === 'O') score += 2;
    });
    
    return score;
  };

  const minimax = (
    squares: Board,
    depth: number,
    alpha: number,
    beta: number,
    isMaximizing: boolean
  ): number => {
    const { winner } = checkWinner(squares);
    
    if (winner || depth === 0 || squares.every(square => square !== null)) {
      return evaluatePosition(squares, depth);
    }

    if (isMaximizing) {
      let maxScore = -Infinity;
      for (let i = 0; i < squares.length; i++) {
        if (!squares[i]) {
          squares[i] = 'O';
          const score = minimax(squares, depth - 1, alpha, beta, false);
          squares[i] = null;
          maxScore = Math.max(maxScore, score);
          alpha = Math.max(alpha, score);
          if (beta <= alpha) break;
        }
      }
      return maxScore;
    } else {
      let minScore = Infinity;
      for (let i = 0; i < squares.length; i++) {
        if (!squares[i]) {
          squares[i] = 'X';
          const score = minimax(squares, depth - 1, alpha, beta, true);
          squares[i] = null;
          minScore = Math.min(minScore, score);
          beta = Math.min(beta, score);
          if (beta <= alpha) break;
        }
      }
      return minScore;
    }
  };

  const getAIMove = (currentBoard: Board): number => {
    const depthMap = {
      easy: 2,
      medium: 4,
      hard: 6
    };
    const maxDepth = depthMap[difficulty as keyof typeof depthMap];
    let bestScore = -Infinity;
    let bestMove = 0;
    const moves: number[] = [];
    const scores: number[] = [];
    
    for (let i = 0; i < currentBoard.length; i++) {
      if (!currentBoard[i]) {
        currentBoard[i] = 'O';
        const score = minimax(currentBoard, maxDepth, -Infinity, Infinity, false);
        currentBoard[i] = null;
        moves.push(i);
        scores.push(score);

        if (score > bestScore) {
          bestScore = score;
          bestMove = i;
        }
      }
    }

    // Add randomness for easy mode
    if (difficulty === 'easy') {
      const goodMoves = moves.filter((_, index) => scores[index] >= bestScore - 5);
      if (goodMoves.length > 0) {
        return goodMoves[Math.floor(Math.random() * goodMoves.length)];
      }
    }
    
    return bestMove;
  };

  useEffect(() => {
    if (!isPlayerTurn && !gameOver) {
      const timer = setTimeout(() => {
        const aiMove = getAIMove([...board]);
        const newBoard = [...board];
        newBoard[aiMove] = 'O';
        setBoard(newBoard);
        setLastMove(aiMove);

        const { winner, line } = checkWinner(newBoard);
        if (winner || newBoard.every(cell => cell !== null)) {
          setWinLine(line);
          setGameOver(true);
          
          // Add delay before showing result
          setTimeout(() => {
            setShowResult(true);
            if (winner) {
              onLose();
            } else {
              onDraw();
            }
          }, 2000);
          return;
        }

        setIsPlayerTurn(true);
      }, 500);

      return () => clearTimeout(timer);
    }
  }, [isPlayerTurn, gameOver, board, difficulty]);

  const handleClick = (index: number) => {
    if (!isPlayerTurn || board[index] || gameOver) return;

    const newBoard = [...board];
    newBoard[index] = 'X';
    setBoard(newBoard);
    setLastMove(index);

    const { winner, line } = checkWinner(newBoard);
    if (winner || newBoard.every(cell => cell !== null)) {
      setWinLine(line);
      setGameOver(true);
      
      // Add delay before showing result
      setTimeout(() => {
        setShowResult(true);
        if (winner) {
          onWin();
        } else {
          onDraw();
        }
      }, 2000);
      return;
    }

    setIsPlayerTurn(false);
  };

  return (
    <div className="flex flex-col items-center gap-4">
      <div className="grid grid-cols-3 gap-2 bg-gray-800 p-4 rounded-lg">
        {board.map((cell, index) => (
          <button
            key={index}
            className={`w-16 h-16 flex items-center justify-center bg-gray-700 rounded-lg 
              hover:bg-gray-600 transition-colors
              ${lastMove === index ? 'ring-2 ring-yellow-400' : ''}
              ${winLine?.includes(index) ? 'ring-2 ring-green-400' : ''}`}
            onClick={() => handleClick(index)}
            disabled={!isPlayerTurn || gameOver}
          >
            {cell === 'X' ? (
              <X className="w-8 h-8 text-cyan-400" />
            ) : cell === 'O' ? (
              <Circle className="w-8 h-8 text-pink-500" />
            ) : null}
          </button>
        ))}
      </div>
      <div className="text-cyan-400 font-mono">
        {gameOver && showResult ? (
          <span className="text-xl">Game Over!</span>
        ) : gameOver ? (
          <span className="animate-pulse">Calculating result...</span>
        ) : (
          <span>{isPlayerTurn ? 'Your Turn' : 'AI Thinking...'}</span>
        )}
      </div>
    </div>
  );
}